import { Add, Message } from '@mui/icons-material';
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Tooltip,
} from '@mui/material';
import { white } from '@styles/colors';
import React, { useState } from 'react';
import MessageTeamDialog from './MessageTeamDialog';
import { Position } from '@staffedupusa/shared-components';
import AddTeamMemberModal from './AddTeamMemberModal';
import BulkTeamActionModal from './BulkTeamActionModal';

function MyTeamBulkActions({
  localJobPostings,
  allApplicationsIds,
  selectedApplicationIds,
  setSelectedApplicationIds,
  handleReset,
  hiredAction,
  disabled,
}: {
  localJobPostings?: Position[];
  allApplicationsIds: number[];
  hiredAction?: string;
  disabled: boolean;
  setSelectedApplicationIds: React.Dispatch<React.SetStateAction<number[]>>;
  selectedApplicationIds: number[];
  handleReset: () => void;
}) {
  const [messageTeamModalOpen, setMessageTeamModalOpen] =
    useState<boolean>(false);
  const [addTeamMemberModalOpen, setAddTeamMemberModalOpen] = useState(false);
  const [selectedBulkAction, setSelectedBulkAction] = useState<
    'terminate' | 'resign' | 'team' | ''
  >('');

  const handleToggleAllPositions = () => {
    if (
      selectedApplicationIds.length === allApplicationsIds?.length &&
      allApplicationsIds?.length !== 0
    ) {
      setSelectedApplicationIds([]);
    } else if (allApplicationsIds?.length !== 0) {
      setSelectedApplicationIds([...allApplicationsIds]);
    }
  };
  const handleBulkActionChange = (event: SelectChangeEvent<string>) => {
    const action = event.target.value as 'terminate' | 'resign' | 'team';
    setSelectedBulkAction(action);
  };
  return (
    <Stack
      bgcolor={white}
      borderRadius="8px"
      p={3}
      alignItems="center"
      maxWidth="100%"
      spacing={3}
      mb={2}
      direction={{ xs: 'column', md: 'row' }}
      justifyContent={{ xs: 'center', md: 'space-between' }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="center"
        alignItems="center"
      >
        <FormControlLabel
          sx={{ minWidth: '130px' }}
          control={
            <Checkbox
              checked={
                selectedApplicationIds.length === allApplicationsIds?.length
              }
              indeterminate={
                selectedApplicationIds.length > 0 &&
                selectedApplicationIds.length <
                  (allApplicationsIds?.length ?? 0)
              }
              onChange={handleToggleAllPositions}
              disabled={disabled || !allApplicationsIds?.length || hiredAction !== 'All'}
            />
          }
          label={`Select All ${selectedApplicationIds && selectedApplicationIds?.length > 0 ? selectedApplicationIds?.length : ''}`}
        />
        <Tooltip
          title={
            hiredAction !== 'All'
              ? 'You can only perform bulk actions on "My Team" applications'
              : !selectedApplicationIds.length
                ? 'Please select application to perform action'
                : ''
          }
          arrow
          placement="top"
        >
          <FormControl
            sx={{ minWidth: { xs: '100%', md: '150px', xl: '230px' } }}
            size="small"
            variant="outlined"
            fullWidth
            disabled={!selectedApplicationIds.length || hiredAction !== 'All'}
          >
            <InputLabel
              id="bulk-actions-input"
              style={{ fontSize: '14px', lineHeight: '14px' }}
            >
              Bulk Actions
            </InputLabel>
            <Select
              size="small"
              label="Bulk Actions"
              labelId="bulk-actions-input"
              value={selectedBulkAction}
              onChange={handleBulkActionChange}
              sx={{ fontSize: '14px', borderRadius: 5, height: '32px' }}
              MenuProps={{
                PaperProps: {
                  style: {
                    borderRadius: 5,
                  },
                },
              }}
            >
              <MenuItem value="">Select action</MenuItem>
              <MenuItem value="terminate">Terminate</MenuItem>
              <MenuItem value="resign">Resign</MenuItem>
            </Select>
          </FormControl>
        </Tooltip>
      </Stack>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="center"
        alignItems="center"
        spacing={2}
      >
        <Button
          sx={{
            fontSize: '14px !important',
          }}
          variant="contained"
          color="primary"
          size="small"
          startIcon={<Message sx={{ fontSize: '16px !important' }} />}
          onClick={() => setMessageTeamModalOpen(true)}
        >
          Message Team
        </Button>
        <Button
          variant="contained"
          size="small"
          color="purple"
          startIcon={<Add sx={{ fontSize: '16px !important' }} />}
          sx={{ height: 'fit-content', fontSize: '14px !important' }}
          onClick={() => setAddTeamMemberModalOpen(true)}
        >
          Add a Team Member
        </Button>
      </Stack>
      {messageTeamModalOpen && (
        <MessageTeamDialog
          open={messageTeamModalOpen}
          onClose={() => setMessageTeamModalOpen(false)}
          positions={
            localJobPostings
              ? localJobPostings?.filter((item) => item.status !== 4)
              : []
          }
        />
      )}
      {addTeamMemberModalOpen && (
        <AddTeamMemberModal
          isOpen={addTeamMemberModalOpen}
          handleClose={() => setAddTeamMemberModalOpen(false)}
          localJobPostings={localJobPostings ? localJobPostings : []}
        />
      )}
      {Boolean(selectedBulkAction) && (
        <BulkTeamActionModal
          isOpen={Boolean(selectedBulkAction)}
          handleClose={() => {
            setSelectedBulkAction('');
            setSelectedApplicationIds([]);
          }}
          selectedApplicationIds={selectedApplicationIds}
          action={selectedBulkAction as 'terminate' | 'resign' | 'team'}
          handleReset={handleReset}
        />
      )}
    </Stack>
  );
}

export default MyTeamBulkActions;
